import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';

// PACKAGES
import { useHistory, useParams } from 'react-router-dom';

// COMPONENTS
import Button from '../../components/Button';
import DisplayDate from '../../components/DisplayDate';
import GlobalModal from '../../components/GlobalModal';
import AdminsModal from './AdminsModal';
import LoadingSpinner from '../../components/LoadingSpinner';
import CircleImage from '../../components/CircleImage';
import DeActivateModal from './DeActivateModal';
import EnableModal from './EnableModal';
import DisplayDateTime from '../../components/DisplayDateTime';
import DisplayPhoneNumber from '../../components/DisplayPhoneNumber';

// TYPES
import { OrganisationDetailsOrganisation, DomainOrganisationStatus } from '../../api/models';

const CompaniesDetail = () => {
	const history = useHistory();

	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	// State to control Modal
	const [showAdminsModal, setShowAdminsModal] = React.useState<boolean>(false);

	const [showDeActivateModal, setShowDeActivateModal] = React.useState<boolean>(false);

	const [showEnableModal, setShowEnableModal] = React.useState<boolean>(false);

	// State for clean up function
	const [isMounted, setIsMounted] = React.useState<boolean>(false);

	const [isActive, setIsActive] = React.useState<boolean>(false);

	// Get the id from params.
	const params = useParams<{ id: string }>();

	// Set up data state and initial data
	const initialData: OrganisationDetailsOrganisation = {
		id: undefined,
		status: undefined,
		name: undefined,
		type: undefined,
		logo: undefined,
		australianBusinessNumber: undefined,
		numberOfChildren: undefined,
		address1: undefined,
		address2: undefined,
		phoneNumber: undefined,
		email: undefined,
		city: undefined,
		postcode: undefined,
		state: undefined,
		administrators: undefined,
		payments: [],
		statusDate: undefined
	};

	const [companiesDetailData, setCompaniesDetailData] = React.useState<OrganisationDetailsOrganisation>(initialData);

	const getCompaniesDetailData = async () => {
		setIsLoading(true);

		setIsMounted(true);

		const response = await apibridge.getCompaniesDetailData(params.id);

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else if (response.result) {
			setIsLoading(false);

			setIsActive(response.result.status === DomainOrganisationStatus.Active);

			setCompaniesDetailData(response.result);
		} else {
			setIsLoading(false);
		}
	};

	React.useEffect(() => {
		getCompaniesDetailData();
	}, []);

	const {
		status,
		name,
		type,
		logo,
		australianBusinessNumber,
		numberOfChildren,
		address1,
		address2,
		phoneNumber,
		email,
		city,
		postcode,
		state,
		administrators,
		payments,
		amount,
		paymentCycle,
		pricePerChild,
		statusDate
	} = companiesDetailData;

	// Format ABN
	const formatABN = (abn?: string) => {
		if (abn) {
			const parts = abn.toString().split('.');

			parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

			return parts.join('.');
		}
	};

	return (
		<div className="page-general-layout settings-page">
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<>
					{isLoading ? (
						<LoadingSpinner show={isLoading} />
					) : (
						<>
							{status === DomainOrganisationStatus.Inactive && (
								<div className="deactive-message">
									<h2 className="main-purple-text mbtom-8">This company is deactivated</h2>

									{statusDate && (
										<p className="sub-grey-text">
											on <DisplayDateTime>{statusDate}</DisplayDateTime>
										</p>
									)}
								</div>
							)}

							{status === DomainOrganisationStatus.Pending && (
								<div className="deactive-message">
									<h2 className="main-purple-text">This company is incomplete</h2>
								</div>
							)}

							<CircleImage size={80} src={logo} alt="organisation logo" />

							<h2 className="header-text settings-page">{name}</h2>

							<div className="general-info">
								<span className="header-info">{type}</span>

								{type && australianBusinessNumber && <div className="header-dot"></div>}

								<span className="header-info">{formatABN(australianBusinessNumber)}</span>
							</div>

							<article className={`children-quantity w-100 mtop-40 ${isActive ? '' : 'deactivated'}`}>
								<label htmlFor="childrenQuantityInput" className="children-quantity-title">
									Number of children at your organisation
								</label>

								<div className="number-container">
									<div className="quantity-input">{numberOfChildren}</div>
								</div>
							</article>

							<div className={`companies-detail-section ${isActive ? '' : 'deactivated'}`}>
								<h2 className="detail-title">Details</h2>

								<ul>
									<li className="row detail-row">
										<div className="col-4 detail-name">ABN</div>

										<div className="col-8 detail-info">{formatABN(australianBusinessNumber)}</div>
									</li>

									<li className="row detail-row">
										<div className="col-4 detail-name">Phone number</div>

										<div className="col-8 detail-info">
											<DisplayPhoneNumber>{phoneNumber}</DisplayPhoneNumber>
										</div>
									</li>

									<li className="row detail-row">
										<div className="col-4 detail-name">Email</div>

										<div className="col-8 detail-info">{email}</div>
									</li>

									<li className="row detail-row">
										<div className="col-4 detail-name">Address line 1</div>

										<div className="col-8 detail-info">{address1}</div>
									</li>

									<li className="row detail-row">
										<div className="col-4 detail-name">Address line 2</div>

										<div className="col-8 detail-info">{address2}</div>
									</li>

									<li className="row detail-row">
										<div className="col-4 detail-name">City</div>

										<div className="col-8 detail-info">{city}</div>
									</li>

									<li className="row detail-row">
										<div className="col-4 detail-name">State</div>

										<div className="col-8 detail-info">{state}</div>
									</li>

									<li className="row detail-row">
										<div className="col-4 detail-name">Postcode</div>

										<div className="col-8 detail-info">{postcode}</div>
									</li>

									<li className="row detail-row">
										<div className="col-4 detail-name">ChildAware Leaders</div>

										<div className="col-8 detail-info">
											{administrators}

											{isActive && (
												<Button className="btn-text pdr-0 pdt-0 pdb-0" onClick={() => setShowAdminsModal(true)}>
													Manage
												</Button>
											)}
										</div>
									</li>

									<li className="row detail-row">
										<div className="col-4 detail-name">Subscription</div>

										<div className="col-8 detail-info">
											{amount && (
												<>
													{type} Plan, ${amount} &nbsp;
													<span className="extra-info">{paymentCycle}</span>
												</>
											)}
										</div>
									</li>
								</ul>
							</div>

							{payments && payments.length > 0 && (
								<div className="billing-tab">
									<div className={`payment-history ${isActive ? '' : 'deactivated'}`}>
										<h2 className="payment-title">Payment History</h2>

										<div className="row table-header">
											<div className="col-9 col-lg-10 col-name">Date</div>

											<div className="col-3 col-lg-2 col-name text-align-right">Amount</div>
										</div>

										<ul>
											{payments.map((item, index) => {
												return (
													<li key={index} className="row table-row">
														<div className="col-9 col-lg-10 payment-date">
															<DisplayDate>{item.paymentDate}</DisplayDate>
														</div>

														<div className="col-3 col-lg-2 payment-amount align-right">${item.amount?.toFixed(2)}</div>
													</li>
												);
											})}
										</ul>
									</div>
								</div>
							)}

							{status === DomainOrganisationStatus.Inactive && (
								<div className="form-button-group">
									<Button className="btn-primary" onClick={() => setShowEnableModal(true)}>
										Enable organisation
									</Button>
								</div>
							)}

							{status === DomainOrganisationStatus.Active && (
								<div className="form-button-group">
									<Button className="btn-primary" onClick={() => setShowDeActivateModal(true)}>
										Deactivate organisation
									</Button>
								</div>
							)}

							<GlobalModal isOpen={showAdminsModal} setIsOpen={setShowAdminsModal} titleText="ChildAware Leaders">
								<AdminsModal name={name} id={companiesDetailData.id} getCompaniesDetailData={getCompaniesDetailData} />
							</GlobalModal>

							<GlobalModal isOpen={showEnableModal} setIsOpen={setShowEnableModal} titleText="Enable organisation">
								<EnableModal companiesDetailData={companiesDetailData} />
							</GlobalModal>

							<GlobalModal
								isOpen={showDeActivateModal}
								setIsOpen={setShowDeActivateModal}
								titleText="Deactivate organisation"
							>
								<DeActivateModal companiesDetailData={companiesDetailData} />
							</GlobalModal>
						</>
					)}
				</>
			)}
		</div>
	);
};

export default CompaniesDetail;
